import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments';
import { BillingHistory, BillingInfo, ConsumableMixMatchPayment, PaymentMethod, PaymentMethodUpdate } from '../models';
import { map, Observable } from 'rxjs';
import { BalanceTransaction, StripeCheckoutSessionStatus, StripeCustomer } from 'app/core/models/stripe';
import { BaseLoadTableResult } from 'app/core/api-clients/baseLoadTableResult';
import { BaseLoadTableParams } from 'app/core/api-clients/baseLoadTableParams';
import { parseDate } from 'app/core/utils/parse-date';

const basePath = `${environment.apiBaseUrl}stripe`;

@Injectable({ providedIn: 'root' })
export class StripeApiClient {
    private readonly _http = inject(HttpClient);

    getPaymentMethods(): Observable<PaymentMethod[]> {
        return this._http.get<PaymentMethod[]>(`${basePath}/customer/payment-methods`);
    }

    getPublishableKey(): Observable<string> {
        return this._http.get<string>(`${basePath}/publishable-key`);
    }

    getPaymentMethodBillingAddress(id: string): Observable<BillingInfo> {
        return this._http.get<BillingInfo>(`${basePath}/customer/payment-method/${id}/billing-address`);
    }

    detachPaymentMethod(id: string) {
        return this._http.delete(`${basePath}/customer/payment-method/${id}`);
    }

    updatePaymentMethod(id: string, model: PaymentMethodUpdate) {
        return this._http.patch(`${basePath}/customer/payment-method/${id}`, model);
    }

    setupIntent(): Observable<string> {
        return this._http.get<string>(`${basePath}/setup-intent`);
    }

    selectDefaultPaymentMethod(defaultPaymentMethodId: any) {
        return this._http.patch(`${basePath}/customer/default-payment-method/${defaultPaymentMethodId}`, {});
    }

    applyPromoCode(promoCode: string, userId?: string | null): Observable<any> {
        return userId == null
            ? this._http.post(`${basePath}/current-user/apply-promo-code`, JSON.stringify(promoCode), {
                  headers: {
                      'Content-Type': 'application/json',
                  },
              })
            : this._http.post(`${basePath}/${userId}/apply-promo-code/`, JSON.stringify(promoCode));
    }

    getCustomer(userId?: string | null): Observable<StripeCustomer> {
        return (
            userId == null
                ? this._http.get<StripeCustomer>(`${basePath}/customer`)
                : this._http.get<StripeCustomer>(`${basePath}/customer/${userId}`)
        ).pipe(map(c => ({ ...c, balance: -c.balance })));
    }

    getBalanceTransactions(
        filter: BaseLoadTableParams,
        userId?: string | null
    ): Observable<BaseLoadTableResult<BalanceTransaction>> {
        return this._http
            .post<
                BaseLoadTableResult<BalanceTransaction>
            >(userId ? `${basePath}/customer/balance-transactions/${userId}` : `${basePath}/customer/balance-transactions`, filter)
            .pipe(
                map(p => ({
                    ...p,
                    data: p.data.map(d => ({
                        ...d,
                        date: parseDate(d.date),
                        amount: -d.amount,
                        endingBalance: -d.endingBalance,
                    })),
                }))
            );
    }
    getBillingHistory(
        filter: BaseLoadTableParams,
        userId?: string | null): Observable<BaseLoadTableResult<BillingHistory>> {
        return this._http
            .post<BaseLoadTableResult<BillingHistory>>(userId ? `${basePath}/customer/billing-history/${userId}` : `${basePath}/customer/billing-history`, filter)
            .pipe(
                map(p => ({
                    ...p,
                    data: p.data.map(d => ({
                        ...d,
                        date: parseDate(d.date),
                    })),
                }))
            );
    }
    getConsumableMixMatchPayements(userId?: string | null): Observable<ConsumableMixMatchPayment[]> {
        return (
            userId == null
                ? this._http.get<ConsumableMixMatchPayment[]>(`${basePath}/consumable-mix-match-payments`)
                : this._http.get<ConsumableMixMatchPayment[]>(`${basePath}/consumable-mix-match-payments/${userId}`)
        ).pipe(map(payments =>
            payments.map(p => ({
                ...p,
                paidDate: parseDate(p.paidDate),
                consumedDate: p.consumedDate ? parseDate(p.consumedDate) : undefined,
                balanceCount: p.reservationCount - p.consumedCount,
            }))
        ));
    }
    createCheckoutSession(userId: string): Observable<string> {
      const url = location.href;
        return this._http.post<string>(`${basePath}/create-checkout-session`, {
          userId,
          priceId: environment.stripe.moment5PackPriceId,
          invoiceDescription: 'Moment 5-Pack Purchase',
          returnUrl: url + (url.includes('?') ? '&' : '?') + 'checkout_session_id={CHECKOUT_SESSION_ID}'
        });
    }
    getCheckoutStatus(sessionId: string) {
      return this._http.get<StripeCheckoutSessionStatus>(`${basePath}/checkout-session-status?session_id=${sessionId}`);
    }
}
